<template>
    <div class="map-container">
        <div id="online-map" class="online-map"></div>
        <div id="popup" class="ol-popup">
            <div id="popup-header" class="popup-header">
                <a href="#" id="popup-print" @click="printMap" class="popup-print"><i class="fas fa-print"></i></a>
                <a href="#" id="popup-closer" @click="clearSelection" class="ol-popup-closer"><i class="fas fa-times"></i></a>
                <span id="header-content"></span>
            </div>
            <div id="popup-content"></div>
        </div>
    </div>
</template>

<script>
    import {Map, View} from "ol";
    import BingMaps from "ol/source/BingMaps";
    import {unByKey} from "ol/Observable";
    import TileLayer from "ol/layer/Tile";
    import VectorImageLayer from "ol/layer/Vector";
    import { Image as ImageLayer } from "ol/layer"
    import XYZ from 'ol/source/XYZ';
    import Attribution from 'ol/control/Attribution';
    import ScaleLine from 'ol/control/ScaleLine';
    import ZoomSlider from 'ol/control/ZoomSlider';
    import Zoom from 'ol/control/Zoom';
    import FullScreen from 'ol/control/FullScreen';
    import {DragPan, DragBox} from 'ol/interaction';
    import Kinetic from 'ol/Kinetic';
    import Rect2D from '../geo/Rect2D';
    import LayerSwitcher from 'ol-layerswitcher';
    import Group from 'ol/layer/Group';
    import OSM from 'ol/source/OSM';
    import WKT from 'ol/format/WKT';
    import Vector from 'ol/source/Vector';
    import Style from 'ol/style/Style';
    import Stroke from 'ol/style/Stroke';
    import Collection from 'ol/Collection';
    import Feature from 'ol/Feature';
    import Overlay from 'ol/Overlay';
    import {platformModifierKeyOnly} from 'ol/events/condition';
    import _ from 'lodash';
    import Units, {METERS_PER_UNIT} from "ol/proj/Units";
    import {toLonLat} from "ol/proj";
    import {format as formatCoordinate} from 'ol/coordinate';
    import {default as ControlBar} from "ol-ext/control/Bar"
    import Toggle from "ol-ext/control/Toggle";
    import {getArea, getLength} from 'ol/sphere';
    import VectorSource from "ol/source/Vector";
    import ImageWMS from "ol/source/ImageWMS";
    import VectorLayer from "ol/layer/Vector";
    import Fill from "ol/style/Fill";
    import CircleStyle from "ol/style/Circle";
    import Draw from "ol/interaction/Draw";
    import Polygon from "ol/geom/Polygon";
    import LineString from "ol/geom/LineString";
    import Button from "ol-ext/control/Button";

    import 'ol-ext/control/Bar.css';
    import '@fortawesome/fontawesome-free/css/solid.css'
    import '@fortawesome/fontawesome-free/js/fontawesome';
    import '@fortawesome/fontawesome-free/js/solid';


    export default {
        name: "OnlineMap",
        props: {
            results: Array,
            centerPoint: String,
            shouldReplace: Boolean,
            selectedIndex: Number,
            shouldZoom: Boolean,
            resizeMap: Number,
        },
        data () {
            return {
                usaMapExtent: '-13985234 2719924,-7352828 6440332.5',
                map: null,
                highlightLayer: null,
                overlay: null,
                container: null,
                content: null,
                closer: null,
                header: null,
                dragBox: null,
                zoomNext: true,
                rulerControl: null,
                areaControl: null,
                measuring: false,
                measureSource: new VectorSource(),
                measureVector: null,
                measureSketch: null,
                measureTooltip: null,
                measureTooltipElement: null,
                addedMeasurementTooltips: [],
                clearMeasurements: null,
                controlToolbar: null,
                parcelLabelsGroup: null,
                layerSwitcher: null
            }
        },
        computed: {
            /**
             * The default map extent that we should center on at initialization
             *
             * @todo Can set default extent here if a more specific one is given
             * @returns {string}
             */
            defaultExtent () {
                return this.usaMapExtent;
            },
        },
        mounted () {
            this.container = document.getElementById('popup');
            this.closer = document.getElementById('popup-closer');
            this.content = document.getElementById('popup-content');
            this.header = document.getElementById('header-content');

            this.closer.onclick = () => {
                this.overlay.setPosition(undefined);
                this.closer.blur();
                this.content.innerHTML = '';
                return false;
            };

            this.$nextTick(function () {
                this.overlay = new Overlay({
                    element: this.container,
                    autoPan: true,
                    autoPanAnimation: {
                        duration: 250
                    }
                });
                this.$nextTick(() => {
                    document.onreadystatechange = () => {
                        if (document.readyState === "complete") {
                            this.initMap();
                            this.$emit('map-loaded');
                        }
                    };
                });
            });
        },
        watch: {
            results (newResults) {
                this.highlightResults(newResults)
            },

            selectedIndex (newIndex) {
                if (this.zoomNext && newIndex !== null)
                    this.getDetailsByIndex(newIndex);
                else
                    this.zoomNext = true;
            },

            resizeMap () {
                this.$nextTick(() => {
                    if (this.map !== null)
                        this.map.updateSize();
                })
            },

            addedMeasurementTooltips (tooltips) {
                if (this.clearMeasurements === null)
                    return;
                if (tooltips.length > 2) {
                    this.clearMeasurements.setVisible(true)
                } else {
                    this.clearMeasurements.setVisible(false);
                }
            }
        },
        methods: {
            initMap: function () {
                const rectObject = new Rect2D(this.defaultExtent.split(/[ ,]/));
                const extentArray = [rectObject.minx,rectObject.miny,rectObject.maxx,rectObject.maxy];

                const mapView = new View({
                    maxZoom: 21
                });

                const layers = [];

                const reportAllLayer = new TileLayer({
                    source: new XYZ({
                        url: 'https://reportallusa.com/dyn/tile.py?mode=tile&tilemode=gmap&tile={x}+{y}+{z}&client=gtqa3GzaMe&map=siteroot/Street_Map.map',
                        attributions: ['©2020 Real Estate Portal USA LLC'],
                        crossOrigin: '*'
                    }),
                    title: 'Street Map',
                    type: 'base',
                    preload: Infinity,
                    updateWhileAnimating: false,
                    updateWhileInteracting: false,
                });
                reportAllLayer.setZIndex(0);

                const openStreetMapLayer = new TileLayer({
                    title: 'Open StreetMap',
                    type: 'base',
                    visible: false,
                    source: new OSM,
                    preload: Infinity,
                    updateWhileAnimating: false,
                    updateWhileInteracting: false,
                });
                openStreetMapLayer.setZIndex(0);

                const bingAerialLayer = new TileLayer({
                    source: new BingMaps({
                        //hidpi: true,
                        key: 'Akdtp2L_EfE9b2CDjO-cJ-77HLcTvZ8HgzCX-EfCLg9AHmIeI8-6Yq_3CXkppq3v',
                        imagerySet: 'AerialWithLabels',
                        maxZoom: 19
                    }),
                    title: 'Bing Aerial',
                    type: 'base',
                    visible: false,
                    preload: Infinity,
                    updateWhileAnimating: false,
                    updateWhileInteracting: false,
                });
                bingAerialLayer.setZIndex(0);

                const googleLayers = [];

                const googleHybridLayer = new TileLayer({
                    source: new XYZ({
                        url: 'https://mt1.google.com/vt/lyrs=y&x={x}&y={y}&z={z}',
                        attributions: ['Map data ©2020 Google.'],
                        crossOrigin: '*'
                    }),
                    title: 'Google Hybrid',
                    type: 'base',
                    visible: false,
                    preload: Infinity,
                    updateWhileAnimating: false,
                    updateWhileInteracting: false,
                });
                googleHybridLayer.setZIndex(0);

                const googleSatelliteLayer = new TileLayer({
                    source: new XYZ({
                        url: 'https://mt1.google.com/vt/lyrs=s&x={x}&y={y}&z={z}',
                        attributions: ['Map data ©2020 Google.'],
                        crossOrigin: '*'
                    }),
                    title: 'Google Satellite',
                    type: 'base',
                    visible: false,
                    preload: Infinity,
                    updateWhileAnimating: false,
                    updateWhileInteracting: false,
                });
                googleSatelliteLayer.setZIndex(0);

                const googleTerrainLayer = new TileLayer({
                    source: new XYZ({
                        url: 'https://mt1.google.com/vt/lyrs=p&x={x}&y={y}&z={z}',
                        attributions: ['Map data ©2020 Google.'],
                        crossOrigin: '*'
                    }),
                    title: 'Google Terrain',
                    type: 'base',
                    visible: false,
                    preload: Infinity,
                    updateWhileAnimating: false,
                    updateWhileInteracting: false,
                });
                googleTerrainLayer.setZIndex(0);

                const googleRoadmapLayer = new TileLayer({
                    source: new XYZ({
                        url: 'https://mt1.google.com/vt/lyrs=m&x={x}&y={y}&z={z}',
                        attributions: ['Map data ©2020 Google.'],
                        crossOrigin: '*'
                    }),
                    title: 'Google Roadmap',
                    type: 'base',
                    visible: false,
                    preload: Infinity,
                    updateWhileAnimating: false,
                    updateWhileInteracting: false,
                });
                googleRoadmapLayer.setZIndex(0);

                googleLayers.push(googleRoadmapLayer);
                googleLayers.push(googleTerrainLayer);
                googleLayers.push(googleSatelliteLayer);
                googleLayers.push(googleHybridLayer);

                const googleLayer = new Group({
                    'title': 'Google',
                    'fold': 'closed',
                    layers: googleLayers
                });

                layers.push(openStreetMapLayer);
                layers.push(bingAerialLayer);
                layers.push(googleLayer);
                layers.push(reportAllLayer);

                this.map = new Map({
                    target: 'online-map',
                    controls: [],
                    view: mapView,
                    overlays: [this.overlay],
                    loadTilesWhileAnimating: true,
                    loadTilesWhileInteracting: true,
                });

                const attribution = new Attribution({
                    collapsible: false
                });

                const touchNavigationControl = new DragPan({
                    options: {
                        kinetic: new Kinetic
                    }
                });

                this.controlToolbar = new ControlBar();
                this.map.addControl(this.controlToolbar);

                this.layerSwitcher = new LayerSwitcher({
                    activationMode: 'click',
                    groupSelectStyle: 'none'
                });

                this.map.addControl(new ScaleLine);
                this.map.addControl(new ZoomSlider);
                this.map.addControl(new Zoom);
                this.map.addControl(attribution);
                this.map.addControl(touchNavigationControl);
                this.map.addControl(this.layerSwitcher);

                this.rulerControl = new Toggle({
                    html: '<i class="fas fa-ruler-horizontal"></i>',
                    className: 'toolbar-button',
                    title: 'Distance Measurement',
                    interaction: this.addMeasureInteraction('LineString'),
                    onToggle: this.toggleDistanceMeasurement
                });
                this.areaControl = new Toggle({
                    html: '<i class="fas fa-ruler-combined"></i>',
                    className: 'toolbar-button',
                    title: 'Area Measurement',
                    interaction: this.addMeasureInteraction('Polygon'),
                    onToggle: this.toggleAreaMeasurement
                });
                this.clearMeasurements = new Button({
                    className: 'toolbar-button',
                    html: '<i class="fas fa-trash-alt"></i>',
                    title: 'Clear measurements',
                    handleClick: this.clearMeasurementLayers
                });
                this.clearMeasurements.setVisible(false);

                this.measureVector = new VectorLayer({
                    source: this.measureSource,
                    style: new Style({
                        fill: new Fill({
                            color: 'rgba(255, 255, 255, 0.2)'
                        }),
                        stroke: new Stroke({
                            color: '#ffcc33',
                            width: 2
                        }),
                        image: new CircleStyle({
                            radius: 7,
                            fill: new Fill({
                                color: '#ffcc33'
                            })
                        })
                    })
                });

                this.controlToolbar.addControl(this.rulerControl);
                this.controlToolbar.addControl(this.areaControl);
                this.controlToolbar.addControl(this.clearMeasurements);
                this.controlToolbar.addControl(new FullScreen);
                this.controlToolbar.setPosition("top");

                this.map.getView().fit(extentArray, this.map.getSize());

                this.populateLayers();
                this.map.addLayer(new Group({
                    'title': 'Base Maps',
                    'fold': 'open',
                    layers: layers
                }));

                this.map.addLayer(this.measureVector);

                this.map.on('click', event => {
                    if (this.measuring)
                        return;
                    this.getDetailsByCoordinate(event);
                });

                this.dragBox = new DragBox({
                    condition: platformModifierKeyOnly
                });

                this.map.addInteraction(this.dragBox);

                this.dragBox.on('boxend', this.getDetailsByExtent);
                this.dragBox.on('boxstart', this.clearSelection);
                this.dragBox.on('boxdrag', this.checkBoxSize);

                // Hook for waiting for zoom to end to refresh the layer-switcher
                let currentZoom = this.map.getView().getZoom();
                this.map.on('moveend', () => {
                    const newZoom = this.map.getView().getZoom();
                    if (currentZoom !== newZoom) {
                        this.layerSwitcher.renderPanel();
                        currentZoom = newZoom;
                    }
                })
            },

            printMap: function () {
                let center = this.map.getView().getCenter();
                let centerLatLon = toLonLat(center);
                let props = {
                    zoom: this.map.getView().getZoom(),
                    center: formatCoordinate(centerLatLon, '{y},{x}', 6)
                };
                this.$emit('print-map', props);
            },

            clearMeasurementLayers: function () {
                this.addedMeasurementTooltips.forEach((tooltip) => {
                    this.map.removeOverlay(tooltip);
                });
                this.addedMeasurementTooltips = [];
                // Must be done twice to emulate startup logic
                this.createMeasureTooltip();
                this.createMeasureTooltip();
                this.measureSource.clear();
            },

            addMeasureInteraction: function (type) {
                let measureInteraction = new Draw({
                    source: this.measureSource,
                    type: type,
                    style: new Style({
                        fill: new Fill({
                            color: 'rgba(255, 255, 255, 0.2)'
                        }),
                        stroke: new Stroke({
                            color: 'rgba(0, 0, 0, 0.5)',
                            lineDash: [10, 10],
                            width: 2
                        }),
                        image: new CircleStyle({
                            radius: 5,
                            stroke: new Stroke({
                                color: 'rgba(0, 0, 0, 0.7)'
                            }),
                            fill: new Fill({
                                color: 'rgba(255, 255, 255, 0.2)'
                            })
                        })
                    })
                });

                this.createMeasureTooltip();

                let listener;
                measureInteraction.on('drawstart', (event) => {
                    this.measureSketch = event.feature;

                    let tooltipCoord = event.coordinate;

                    listener = this.measureSketch.getGeometry().on('change', (event) => {
                        let geom = event.target;
                        let output;
                        if (geom instanceof Polygon) {
                            output = this.formatArea(geom);
                            tooltipCoord = geom.getInteriorPoint().getCoordinates();
                        } else if (geom instanceof LineString) {
                            output = this.formatLength(geom);
                            tooltipCoord = geom.getLastCoordinate();
                        }
                        this.measureTooltipElement.innerHTML = output;
                        this.measureTooltip.setPosition(tooltipCoord);
                    });
                });
                measureInteraction.on('drawend', () => {
                    this.measureTooltipElement.className = 'ol-tooltip ol-tooltip-static';
                    this.measureTooltip.setOffset([0, -7]);
                    this.measureSketch = null;
                    this.measureTooltipElement = null;
                    this.createMeasureTooltip();
                    unByKey(listener);
                })

                return measureInteraction;
            },

            createMeasureTooltip: function () {
                if (this.measureTooltipElement) {
                    this.measureTooltipElement.parentNode.removeChild(this.measureTooltipElement);
                }
                this.measureTooltipElement = document.createElement('div');
                this.measureTooltipElement.classname = 'ol-tooltip ol-tooltip-measure';
                this.measureTooltip = new Overlay({
                    element: this.measureTooltipElement,
                    offset: [0, -15],
                    positioning: 'bottom-center'
                });
                this.addedMeasurementTooltips.push(this.measureTooltip);
                this.map.addOverlay(this.measureTooltip);
            },

            toggleDistanceMeasurement: function (active) {
                if (active) {
                    window.store.setAnnouncementMessageAction("Distance measurement: Click to add points, double-click to measure. Click ruler toolbar button again to finish.");
                    if (this.areaControl.getActive()) {
                        this.areaControl.setActive(false);
                    }
                    this.measuring = true;
                } else {
                    window.store.clearAnnouncementMessageAction();
                    this.measuring = false;
                    if (this.measureTooltip) {
                        this.map.removeOverlay(this.measureTooltip);
                        this.createMeasureTooltip();
                    }
                }
            },

            toggleAreaMeasurement: function (active) {
                if (active) {
                    window.store.setAnnouncementMessageAction("Area measurement: Click to add points, double-click to measure. Click square ruler toolbar button again to finish.");
                    if (this.rulerControl.getActive()) {
                        this.rulerControl.setActive(false);
                    }
                    this.measuring = true;
                } else {
                    window.store.clearAnnouncementMessageAction();
                    this.measuring = false;
                    if (this.measureTooltip) {
                        this.map.removeOverlay(this.measureTooltip);
                        this.createMeasureTooltip();
                    }
                }
            },

            formatLength: function (line) {
                let length = getLength(line);
                let output;
                let feet = length / 0.3048;
                let miles = length / 1609.344;
                output = Math.round(feet) + ' ft<br>';
                output += miles.toPrecision(3) + ' mi';
                return output;
            },

            formatArea: function (polygon) {
                let area = getArea(polygon);
                let output;
                let sqft = area / 0.09290304;
                let sqmi = area / 2589988.1103;
                let acres = area * 0.00024710538;
                output = Math.round(sqft) + ' ft<sup class="sup">2</sup><br>';
                output += sqmi.toPrecision(3) + ' mi<sup class="sup">2</sup><br>';
                output += acres.toPrecision(4) + ' acres';
                return output;
            },

            getResolutionForScale: function (scale) {
                const dpi = window.devicePixelRatio * 96;
                const metersPerUnit = METERS_PER_UNIT[Units.METERS];
                const inchesPerMeter = 39.37;
                return parseFloat(scale) / (metersPerUnit * inchesPerMeter * dpi);
            },

            populateLayers: function () {
                const overlayLayers = [];

                const zoom15Resolution = this.getResolutionForScale(37000);

                const parcelsOverlay = new TileLayer({
                    title: 'Parcels',
                    source: new XYZ({
                        url: 'https://reportallusa.com/dyn/tile.py?mode=tile&tilemode=gmap&tile={x}+{y}+{z}&map=siteroot/Base_Layers.map&layer=Parcels&client=gtqa3GzaMe',
                        attributions: ['©2020 Real Estate Portal USA LLC'],
                        crossOrigin: '*',
                    }),
                    visible: true,
                    maxResolution: zoom15Resolution,
                    zIndex: 10,
                    loadTilesWhileAnimating: false,
                    loadTilesWhileInteracting: false,
                    preload: Infinity
                });

                const buildingsOverlay = new TileLayer({
                    title: 'Buildings',
                    source: new XYZ({
                        url: 'https://reportallusa.com/dyn/tile.py?mode=tile&tilemode=gmap&tile={x}+{y}+{z}&map=siteroot/Base_Layers.map&layer=Buildings&client=gtqa3GzaMe',
                        attributions: ['©2020 Real Estate Portal USA LLC'],
                        crossOrigin: '*',
                    }),
                    visible: true,
                    maxResolution: zoom15Resolution,
                    zIndex: 10,
                    loadTilesWhileAnimating: false,
                    loadTilesWhileInteracting: false,
                    preload: Infinity
                });

                const parcelDimensionsOverlay = new TileLayer({
                    title: 'Parcel Dimensions ft (approx)',
                    source: new XYZ({
                        url: 'https://reportallusa.com/dyn/tile.py?mode=tile&tilemode=gmap&tile={x}+{y}+{z}&map=siteroot/Parcel_Labels_and_Dimensions.map&layer=Parcel Dimensions ft (approx)&client=gtqa3GzaMe',
                        attributions: ['©2020 Real Estate Portal USA LLC'],
                        crossOrigin: '*',
                    }),
                    visible: false,
                    minZoom: 17,
                    maxResolution: this.getResolutionForScale(3000),
                    zIndex: 15,
                    loadTilesWhileAnimating: false,
                    loadTilesWhileInteracting: false,
                    preload: Infinity
                });

                const zipCodesOverlay = new TileLayer({
                    title: 'Zip Codes',
                    source: new XYZ({
                        url: 'https://reportallusa.com/dyn/tile.py?mode=tile&tilemode=gmap&tile={x}+{y}+{z}&map=siteroot/Base_Layers.map&layer=Zip Codes&client=gtqa3GzaMe',
                        attributions: ['©2020 Real Estate Portal USA LLC'],
                        crossOrigin: '*',
                    }),
                    visible: false,
                    maxResolution: this.getResolutionForScale(400000),
                    zIndex: 15,
                    loadTilesWhileAnimating: false,
                    loadTilesWhileInteracting: false,
                    preload: Infinity
                });

                const hydrographyOverlay = new TileLayer({
                    title: 'Hydrography',
                    source: new XYZ({
                        url: 'https://reportallusa.com/dyn/tile.py?mode=tile&tilemode=gmap&tile={x}+{y}+{z}&map=siteroot/Base_Layers.map&layer=Hydrography&client=gtqa3GzaMe',
                        attributions: ['©2020 Real Estate Portal USA LLC'],
                        crossOrigin: '*',
                    }),
                    visible: false,
                    maxResolution: this.getResolutionForScale(500000),
                    zIndex: 10,
                    loadTilesWhileAnimating: false,
                    loadTilesWhileInteracting: false,
                    preload: Infinity
                });

                const powerGridOverlay = new TileLayer({
                    title: 'PowerGrid',
                    source: new XYZ({
                        url: 'https://reportallusa.com/dyn/tile.py?mode=tile&tilemode=gmap&tile={x}+{y}+{z}&map=siteroot/Base_Layers.map&layer=PowerGrid&client=gtqa3GzaMe',
                        attributions: ['©2020 Real Estate Portal USA LLC'],
                        crossOrigin: '*',
                    }),
                    visible: false,
                    zIndex: 10,
                    loadTilesWhileAnimating: false,
                    loadTilesWhileInteracting: false,
                    preload: Infinity
                });

                overlayLayers.push(powerGridOverlay);
                overlayLayers.push(hydrographyOverlay);
                overlayLayers.push(zipCodesOverlay);
                overlayLayers.push(buildingsOverlay);
                overlayLayers.push(parcelDimensionsOverlay);
                overlayLayers.push(parcelsOverlay);

                const overlays = new Group({
                    'title': 'Overlays',
                    'fold': 'open',
                    layers: overlayLayers
                });

                const parcelLabelsLayers = [];

                const label_attributes = ['parcel_id',
                                          'owner',
                                          'owner_surname',
                                          'address',
                                          'addr_number',
                                          'trans_date',
                                          'sale_price',
                                          'bldg_sqft',
                                          'land_use_code',
                                          'mkt_val_tot',
                                          'mkt_val_land',
                                          'mkt_val_bldg',
                                          'story_height',
                                          'routing_num',
                                          'year_built'].reverse();

                // Note: To make labels white for against aerial imagery, add to source url:
                // &map.layer[0].class[0].label[0]=COLOR 255 255 255 SIZE 10 OUTLINECOLOR 0 0 0
                label_attributes.forEach(label_attribute => {
                    const label_attribute_pretty = label_attribute.charAt(0).toUpperCase() + label_attribute.slice(1).replace(/_/g, ' ');
                    const labelLayer = new TileLayer({
                        title: label_attribute_pretty,
                        source: new XYZ({
                            url: 'https://reportallusa.com/dyn/tile.py?mode=tile&tilemode=gmap&tile={x}+{y}+{z}&map=siteroot/Parcel_Labels_and_Dimensions.map&layer=Parcel Labels&client=gtqa3GzaMe&map.layer[0]=LABELITEM ' + label_attribute + '&no_tilecache=1',
                            attributions: ['©2020 Real Estate Portal USA LLC'],
                            crossOrigin: '*',
                        }),
                        visible: false,
                        minZoom: 16,
                        maxResolution: this.getResolutionForScale(10000),
                        zIndex: 15,
                        loadTilesWhileAnimating: false,
                        loadTilesWhileInteracting: false,
                        preload: Infinity
                    });
                    labelLayer.on('change:visible', this.parcelLabelsSelectionChanged);
                    parcelLabelsLayers.push(labelLayer);
                });

                this.parcelLabelsGroup = new Group({
                    'title': 'Parcel Labels',
                    'fold': 'close',
                    layers: parcelLabelsLayers
                });

                const salesHistoryLayers = [];

                const recent5to4Sales = new TileLayer({
                    title: 'Recent Sales(5 to 4 years ago) Where Available',
                    source: new XYZ({
                        url: 'https://reportallusa.com/dyn/tile.py?mode=tile&tilemode=gmap&tile={x}+{y}+{z}&map=siteroot/Sales_History.map&layer=Recent Sales(5 to 4 years ago) Where Available&client=gtqa3GzaMe',
                        attributions: ['©2020 Real Estate Portal USA LLC'],
                        crossOrigin: '*',
                    }),
                    visible: false,
                    maxResolution: zoom15Resolution,
                    zIndex: 15,
                    loadTilesWhileAnimating: false,
                    loadTilesWhileInteracting: false,
                    preload: Infinity
                });

                const recent4to3Sales = new TileLayer({
                    title: 'Recent Sales(4 to 3 years ago) Where Available',
                    source: new XYZ({
                        url: 'https://reportallusa.com/dyn/tile.py?mode=tile&tilemode=gmap&tile={x}+{y}+{z}&map=siteroot/Sales_History.map&layer=Recent Sales(4 to 3 years ago) Where Available&client=gtqa3GzaMe',
                        attributions: ['©2020 Real Estate Portal USA LLC'],
                        crossOrigin: '*',
                    }),
                    visible: false,
                    maxResolution: zoom15Resolution,
                    zIndex: 15,
                    loadTilesWhileAnimating: false,
                    loadTilesWhileInteracting: false,
                    preload: Infinity
                });

                const recent3to2Sales = new TileLayer({
                    title: 'Recent Sales(3 to 2 years ago) Where Available',
                    source: new XYZ({
                        url: 'https://reportallusa.com/dyn/tile.py?mode=tile&tilemode=gmap&tile={x}+{y}+{z}&map=siteroot/Sales_History.map&layer=Recent Sales(3 to 2 years ago) Where Available&client=gtqa3GzaMe',
                        attributions: ['©2020 Real Estate Portal USA LLC'],
                        crossOrigin: '*',
                    }),
                    visible: false,
                    maxResolution: zoom15Resolution,
                    zIndex: 15,
                    loadTilesWhileAnimating: false,
                    loadTilesWhileInteracting: false,
                    preload: Infinity
                });

                const past24MonthSales = new TileLayer({
                    title: 'Recent Sales(Past 24 Mo. to 1 year ago) Where Available',
                    source: new XYZ({
                        url: 'https://reportallusa.com/dyn/tile.py?mode=tile&tilemode=gmap&tile={x}+{y}+{z}&map=siteroot/Sales_History.map&layer=Recent Sales(Past 24 Mo. to 1 year ago) Where Available&client=gtqa3GzaMe',
                        attributions: ['©2020 Real Estate Portal USA LLC'],
                        crossOrigin: '*',
                    }),
                    visible: false,
                    maxResolution: zoom15Resolution,
                    zIndex: 15,
                    loadTilesWhileAnimating: false,
                    loadTilesWhileInteracting: false,
                    preload: Infinity
                });

                const past12MonthSales = new TileLayer({
                    title: 'Recent Sales(Past 12 Mo.) Where Available',
                    source: new XYZ({
                        url: 'https://reportallusa.com/dyn/tile.py?mode=tile&tilemode=gmap&tile={x}+{y}+{z}&map=siteroot/Sales_History.map&layer=Recent Sales(Past 12 Mo.) Where Available&client=gtqa3GzaMe',
                        attributions: ['©2020 Real Estate Portal USA LLC'],
                        crossOrigin: '*',
                    }),
                    visible: false,
                    maxResolution: zoom15Resolution,
                    zIndex: 15,
                    loadTilesWhileAnimating: false,
                    loadTilesWhileInteracting: false,
                    preload: Infinity
                });

                const recentNonDollarSales = new TileLayer({
                    title: 'Recent Non-$ Transfers(Past 5 Yrs)',
                    source: new XYZ({
                        url: 'https://reportallusa.com/dyn/tile.py?mode=tile&tilemode=gmap&tile={x}+{y}+{z}&map=siteroot/Sales_History.map&layer=Recent Non-$ Transfers(Past 5 Yrs)&client=gtqa3GzaMe',
                        attributions: ['©2020 Real Estate Portal USA LLC'],
                        crossOrigin: '*',
                    }),
                    visible: false,
                    maxResolution: zoom15Resolution,
                    zIndex: 15,
                    loadTilesWhileAnimating: false,
                    loadTilesWhileInteracting: false,
                    preload: Infinity
                });

                salesHistoryLayers.push(recentNonDollarSales);
                salesHistoryLayers.push(past12MonthSales);
                salesHistoryLayers.push(past24MonthSales);
                salesHistoryLayers.push(recent3to2Sales);
                salesHistoryLayers.push(recent4to3Sales);
                salesHistoryLayers.push(recent5to4Sales);

                const salesHistory = new Group({
                    'title': 'Sales History',
                    'fold': 'close',
                    layers: salesHistoryLayers
                });

                const webLayers = [];

                const weatherRadar = new ImageLayer({
                    title: 'Weather Radar (Live)',
                    source: new ImageWMS({
                        url: "https://mesonet.agron.iastate.edu/cgi-bin/wms/nexrad/n0r.cgi",
                        params: {
                            'NAME': 'nexrad-n0r',
                            'TITLE': 'US RADAR',
                            'LAYERS': 'nexrad-n0r',
                        },
                        serverType: 'geoserver',
                    }),
                    zIndex: 10,
                    visible: false,
                });

                const femaFloodLayer = new ImageLayer({
                    title: 'FEMA Flood Hazard Boundaries',
                    source: new ImageWMS({
                        url: "https://hazards.fema.gov/gis/nfhl/services/public/NFHLWMS/MapServer/WMSServer",
                        params: {
                            'NAME': 4,
                            'TITLE': 'Flood Hazards Zones',
                            'LAYERS': '4,5',
                        },
                        serverType: 'geoserver',
                    }),
                    zIndex: 10,
                    visible: false,
                    maxResolution: this.getResolutionForScale(47247.023810),
                });

                webLayers.push(femaFloodLayer);
                webLayers.push(weatherRadar);

                const web = new Group({
                    'title': 'Web Layers',
                    'fold': 'close',
                    layers: webLayers
                });

                this.map.addLayer(web);
                this.map.addLayer(salesHistory);
                this.map.addLayer(this.parcelLabelsGroup);
                this.map.addLayer(overlays);
            },

            squareFeetToMiles: function (area) {
                return area / 2.788e+7;
            },

            parcelLabelsSelectionChanged: async function (event) {
                const selected = !event.oldValue;
                if (!selected)
                    return;
                const layer = event.target;
                const layerName = layer.get('title');
                const showLayer = await this.$parent.checkParcelAttributeLayers();
                this.parcelLabelsGroup.getLayers().forEach(groupLayer => {
                    if (groupLayer.get('title') !== layerName || !showLayer) {
                        groupLayer.setVisible(false);
                    }
                });
                this.layerSwitcher.renderPanel();
            },

            checkBoxSize: function (event) {
                const selectBox = event.target;
                const boxArea = selectBox.getGeometry().getArea();
                const dragBoxElement = $('.ol-dragbox');

                const areaSquareMiles = this.squareFeetToMiles(boxArea);

                if (areaSquareMiles > 1.5) {
                    dragBoxElement.css('background-color', 'rgba(228,148,148,0.4)');
                    dragBoxElement.css('border-color', 'rgba(228,148,148,1)');
                } else {
                    dragBoxElement.css('background-color', 'rgba(255,255,255,0.4)');
                    dragBoxElement.css('border-color', 'rgba(100,150,0,1)')
                }
            },

            clearSelection: function () {
                if (this.measuring)
                    return;
                this.$emit('selection-changed', null);
                this.overlay.setPosition(undefined);
                this.closer.blur();
                this.content.innerHTML = '';
            },

            getDetailsByExtent: function () {
                if (this.measuring)
                    return;
                const boxArea = this.dragBox.getGeometry().getArea();
                const areaSquareMiles = this.squareFeetToMiles(boxArea);

                if (areaSquareMiles > 1.5) {
                    return;
                }

                const extent = this.dragBox.getGeometry().getExtent();
                this.$emit('identify-multiple', extent);
            },

            getDetailsByIndex: function (index) {
                if (this.highlightLayer) {
                    let feature = this.highlightLayer.getSource().getFeatureById(index);
                    this.getDetails(feature, true);
                }
            },

            getDetailsByCoordinate: function (event) {
                let feature = null;
                let coordinate = event.coordinate;
                let controlClick = event.originalEvent.ctrlKey || event.originalEvent.metaKey;
                if (this.highlightLayer) {
                    feature = this.highlightLayer.getSource().getFeaturesAtCoordinate(coordinate);
                }
                if (feature && feature.length > 0) {
                    this.zoomNext = false;
                    this.getDetails(feature[0], false);
                    this.$emit('selection-changed', feature[0].getId());
                } else if (feature && this.content.innerHTML !== '') {
                    this.clearSelection();
                } else if (controlClick) {
                    this.$emit('identify-single', coordinate[0], coordinate[1], false);
                } else {
                    this.$emit('identify-single', coordinate[0], coordinate[1], true);
                }
            },

            getDetails: function (feature, shouldZoom = false) {
                let container = this;
                if (feature) {
                    let content = '<table class="table-data">';
                    _.forOwn(feature.getProperties(), function (value, key) {
                        if (key === 'Address') {
                            container.header.innerHTML = value;
                        }
                        if (key === 'Sell') {
                            return;
                        }
                        if (key !== 'geometry' && key !== 'labelPoint' && value) {
                            if (key === 'Parcel Id') {
                                value = '<a style="color:darkgreen;" target="_blank" href="/cama_redir?county_id=' + feature.getProperties()['County Id'] + '&rausa_id=' + feature.getProperties()['County Row Id'] + '&parcel_id=' + feature.getProperties()['Parcel Id'] + '">' + value + '</a>';
                            }
                            content += "<tr>";
                            content += "<td style-size='small'><pre style='white-space: pre-wrap; word-break: keep-all; font-family: sans-serif'>" + key + "</pre></td><td style-size='small' class='table-cell-right-aligned'><pre style='white-space: pre-wrap; word-break: keep-all; font-family: sans-serif'>" + value + "</pre></td>";
                            content += "</tr>";
                        }
                    });
                    content += "</table>";
                    this.content.innerHTML = content;
                    this.content.scrollTop = 0;
                    this.overlay.setPosition(feature.get('labelPoint').getCoordinates());
                    if (shouldZoom) {
                        let geometry = feature.getGeometry();
                        if (geometry) {
                            this.map.getView().fit(feature.getGeometry(), {
                                'size': this.map.getSize(),
                                'padding': [50,50,50,50]
                            });
                        }
                    }
                } else {
                    this.clearSelection();
                }
            },

            highlightResults: async function (results) {
                let features = new Collection;

                results.forEach(function (parcel, index) {
                    if (parcel.geom_as_wkt === null || parcel.labelpt === null) {
                        return;
                    }
                    let geom = (new WKT).readGeometryFromText(parcel.geom_as_wkt);
                    let labelPoint = (new WKT).readGeometryFromText(parcel.labelpt);

                    let feature = new Feature({
                        geometry: geom,
                        labelPoint: labelPoint
                    });

                    _.forOwn(parcel, function (value, key) {
                        let casedKey = _.startCase(key);
                        if (key !== 'geom_as_wkt' && key !== 'labelpt') {
                            feature.setProperties({[casedKey]: value});
                        }
                    });

                    feature.setId(index);
                    features.push(feature);
                });

                if (this.highlightLayer) {
                    let source = new Vector({
                        features: features
                    });
                    this.highlightLayer.setSource(source);
                    this.clearSelection();
                } else {
                    this.highlightLayer = new VectorImageLayer({
                        source: new Vector({
                            features: features
                        }),
                        style: new Style({
                            stroke: new Stroke({
                                color: 'red',
                                width: 3
                            })
                        }),
                        loadTilesWhileAnimating: false,
                        loadTilesWhileInteracting: false,
                        maxResolution: this.getResolutionForScale(37000),
                        title: 'Results'
                    });
                    this.highlightLayer.setZIndex(20);
                    this.map.addLayer(this.highlightLayer);
                }
                if (this.shouldZoom) {
                    let extent = this.highlightLayer.getSource().getExtent();
                    if (extent && extent[0] !== Infinity) {
                        this.map.getView().fit(extent, {
                            'size': this.map.getSize(),
                            'padding': [50,50,50,50]
                        });
                    }
                }
                if (results.length === 0) {
                    const rectObject = new Rect2D(this.defaultExtent.split(/[ ,]/));
                    const extentArray = [rectObject.minx,rectObject.miny,rectObject.maxx,rectObject.maxy];
                    this.map.getView().fit(extentArray, this.map.getSize());
                }
                this.$emit('finished-loading');
            },
        }
    }
</script>

<style scoped>
    .popup-header {
        background-color: #d7e0e4;
        font-size: 1.5em;
        text-align: center;
        border-radius: 10px 10px 0 0;
        padding-top: 5px;
        padding-bottom: 5px;
        font-weight: 600;
        min-height: 24px;
    }
    .map-container {
        height: 100%;
        width: 100%;
    }
    .online-map {
        height: 100%;
        display: flex;
        flex-direction: column;
        flex: 1;
    }
    .ol-popup {
        position: absolute;
        background-color: white;
        -webkit-filter: drop-shadow(0 1px 4px rgba(0,0,0,0.2));
        filter: drop-shadow(0 1px 4px rgba(0,0,0,0.2));
        border-radius: 10px;
        border: 1px solid #cccccc;
        bottom: 12px;
        left: -50px;
        min-width: 380px;
    }
    #popup-content {
        max-height: 200px;
        overflow: auto;
        padding-left: 15px;
        padding-right: 15px;
    }
    .ol-popup:after, .ol-popup:before {
        top: 100%;
        border: solid transparent;
        content: " ";
        height: 0;
        width: 0;
        position: absolute;
        pointer-events: none;
    }
    .ol-popup:after {
        border-top-color: white;
        border-width: 10px;
        left: 48px;
        margin-left: -10px;
    }
    .ol-popup:before {
        border-top-color: #cccccc;
        border-width: 11px;
        left: 48px;
        margin-left: -11px;
    }
    .ol-popup-closer {
        text-decoration: none;
        position: absolute;
        top: 4px;
        right: 8px;
    }
    .popup-print {
        text-decoration: none;
        position: absolute;
        top: 4px;
        left: 8px;
    }
    pre {
        font-family: sans-serif;
    }
</style>
